'use client';

import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import ChevronLeft from '../../../public/assets/icons/paginator/prev.svg';
import ChevronRight from '../../../public/assets/icons/paginator/next.svg';

/** How long a set of columns rests before the rail advances, matching the hero. */
const SLIDE_DURATION = 6000;

/** Dot geometry straight from the Figma paginator. */
const DOT = 'size-[6.915px] rounded-full transition-colors';

const navButton =
  'flex size-8 shrink-0 items-center justify-center rounded-full bg-white/95 shadow-sm transition-colors disabled:cursor-default';

/**
 * Horizontal rail for the news channels.
 *
 * Native scroll-snap does the moving, so a touch swipe works without any of
 * this code running; the buttons, dots and autoplay just drive `scrollTo`. How
 * far one step goes and how many stops there are is measured rather than
 * assumed, because the number of visible columns changes with the breakpoint.
 */
const NewsCarousel = ({ children }: { children: ReactNode }) => {
  const track = useRef<HTMLDivElement>(null);
  const [stops, setStops] = useState(1);
  const [at, setAt] = useState(0);
  const [paused, setPaused] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);

  const stepWidth = useCallback(() => {
    const rail = track.current;
    const first = rail?.firstElementChild;
    if (!rail || !first) return 0;
    const gap = parseFloat(getComputedStyle(rail).columnGap) || 0;
    return first.getBoundingClientRect().width + gap;
  }, []);

  const measure = useCallback(() => {
    const rail = track.current;
    const step = stepWidth();
    if (!rail || step <= 0) return;
    const overflow = rail.scrollWidth - rail.clientWidth;
    setStops(Math.max(1, Math.round(overflow / step) + 1));
    setAt(Math.round(rail.scrollLeft / step));
  }, [stepWidth]);

  useEffect(() => {
    const rail = track.current;
    if (!rail) return;

    measure();
    rail.addEventListener('scroll', measure, { passive: true });

    const observer = new ResizeObserver(measure);
    observer.observe(rail);

    return () => {
      rail.removeEventListener('scroll', measure);
      observer.disconnect();
    };
  }, [measure]);

  useEffect(() => {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const sync = () => setReducedMotion(query.matches);
    sync();
    query.addEventListener('change', sync);
    return () => query.removeEventListener('change', sync);
  }, []);

  useEffect(() => {
    const onVisibility = () => setPaused(document.hidden);
    // Also read it once: a tab restored in the background mounts hidden
    // without ever firing the event.
    onVisibility();
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  const goTo = useCallback(
    (index: number) => {
      const rail = track.current;
      if (!rail) return;
      rail.scrollTo({
        left: index * stepWidth(),
        behavior: reducedMotion ? 'auto' : 'smooth',
      });
    },
    [reducedMotion, stepWidth]
  );

  // Each landing resets the timer, so a swipe or a click buys a full dwell
  // before the rail moves on by itself.
  useEffect(() => {
    if (reducedMotion || paused || stops <= 1) return;
    const id = window.setTimeout(
      () => goTo(at >= stops - 1 ? 0 : at + 1),
      SLIDE_DURATION
    );
    return () => window.clearTimeout(id);
  }, [at, stops, paused, reducedMotion, goTo]);

  const atStart = at <= 0;
  const atEnd = at >= stops - 1;

  return (
    <div
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
    >
      <div
        ref={track}
        className="flex snap-x snap-mandatory [scrollbar-width:none] gap-6 overflow-x-auto [&::-webkit-scrollbar]:hidden"
      >
        {children}
      </div>

      {stops > 1 && (
        <div className="mt-4 flex items-center gap-2">
          <div className="flex flex-1 items-center justify-center gap-[6.915px] pl-18">
            {Array.from({ length: stops }, (_, index) => (
              <button
                key={index}
                type="button"
                onClick={() => goTo(index)}
                aria-label={`Show channels ${index + 1} of ${stops}`}
                aria-current={index === at}
                className={classNames(
                  DOT,
                  index === at ? 'bg-black' : 'bg-[#C1C7CC]'
                )}
              />
            ))}
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => goTo(at - 1)}
              disabled={atStart}
              aria-label="Previous channels"
              className={classNames(
                navButton,
                atStart
                  ? 'border border-[#CDCCCF] text-[#C1C7CC]'
                  : 'border border-[#707278] text-black'
              )}
            >
              <ChevronLeft className="size-4" />
            </button>
            <button
              type="button"
              onClick={() => goTo(at + 1)}
              disabled={atEnd}
              aria-label="Next channels"
              className={classNames(
                navButton,
                atEnd
                  ? 'border border-[#CDCCCF] text-[#C1C7CC]'
                  : 'border border-[#707278] text-black'
              )}
            >
              <ChevronRight className="size-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewsCarousel;
